import { useState } from 'react';
import emailjs from '@emailjs/browser';
import { useParallax } from '../hooks/useParallax';

const Contact = () => {
    const parallaxOffset = useParallax(0.1);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            // EmailJS Configuration - Your actual values
            const serviceId = 'service_6gfhb85';
            const templateId = 'template_43gy5hd';
            const publicKey = 'MdQhnaTxp_3Ar67_6';

            // Initialize EmailJS
            emailjs.init(publicKey);

            // Template parameters for EmailJS
            const templateParams = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                subject: formData.subject,
                message: formData.message
            };


            // Send email via EmailJS - Automatic delivery to your inbox!
            const response = await emailjs.send(serviceId, templateId, templateParams);

            console.log('✅ Email sent successfully via EmailJS:', response);
            setSubmitStatus('success');

            // Reset form after successful submission
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                subject: '',
                message: ''
            });

        } catch (error) {
            console.error('❌ EmailJS sending failed:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section id="contact" className="py-6 px-6 bg-gray-50 dark:bg-black/40 relative overflow-hidden">
            {/* Parallax Background Elements */}
            <div
                className="absolute inset-0 opacity-10"
                style={{ transform: `translateY(${parallaxOffset}px)` }}
            >
                <div className="absolute top-1/4 left-10 w-64 h-64 bg-primary/30 rounded-full blur-3xl"></div>
                <div className="absolute bottom-1/4 right-10 w-80 h-80 bg-purple-500/30 rounded-full blur-3xl"></div>
            </div>

            <div className="container max-w-6xl mx-auto relative z-10">
                <div className="text-center mb-16 space-y-4">
                    <div className="inline-flex items-center gap-2 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark px-3 py-1.5 rounded-full text-xs font-medium">
                        <span className="w-2 h-2 bg-primary rounded-full animate-pulse"></span>
                        Get In Touch
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white">
                        Let's Work <span className="text-primary">Together</span>
                    </h2>
                    <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                        Have a project in mind? Let's discuss how we can bring your ideas to life with modern web technologies.
                    </p>
                </div>

                <div className="grid lg:grid-cols-2 gap-12 items-start">
                    {/* Contact Information */}
                    <div className="space-y-8">
                        <div>
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Contact Information</h3>
                            <div className="space-y-6">
                                <div className="flex items-start gap-4 group">
                                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                                        <span className="material-icons-round text-primary">email</span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Email</h4>
                                        <a href="mailto:mdazaharul725@gmail.com" className="text-gray-600 dark:text-gray-400 hover:text-primary transition-colors">
                                            mdazaharul725@gmail.com
                                        </a>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4 group">
                                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                                        <span className="material-icons-round text-primary">phone</span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Phone</h4>
                                        <a href="tel:+8801317087713" className="text-gray-600 dark:text-gray-400 hover:text-primary transition-colors">
                                            +88013******13
                                        </a>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4 group">
                                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                                        <span className="material-icons-round text-primary">location_on</span>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Location</h4>
                                        <p className="text-gray-600 dark:text-gray-400">
                                            Dhaka, Bangladesh
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Social Links */}
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Follow Me</h3>
                            <div className="flex gap-4">
                                <a
                                    href="https://www.linkedin.com/in/azaharul-islam/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-12 h-12 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-lg flex items-center justify-center hover:border-primary/50 hover:bg-primary/10 transition-all duration-300 group"
                                >
                                    <span className="material-icons-round text-gray-600 dark:text-gray-400 group-hover:text-primary">work</span>
                                </a>
                                <a
                                    href="https://github.com/Azaharul-islam615"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-12 h-12 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-lg flex items-center justify-center hover:border-primary/50 hover:bg-primary/10 transition-all duration-300 group"
                                >
                                    <span className="material-icons-round text-gray-600 dark:text-gray-400 group-hover:text-primary">code</span>
                                </a>
                                <a
                                    href="https://wa.me/8801317087713"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-12 h-12 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-lg flex items-center justify-center hover:border-primary/50 hover:bg-primary/10 transition-all duration-300 group"
                                >
                                    <span className="material-icons-round text-gray-600 dark:text-gray-400 group-hover:text-primary">chat</span>
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Contact Form */}
                    <div className="bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-2xl p-8">
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Send Message</h3>

                        {/* Email Flow Indicator */}
                       

                        {/* Success Message */}
                        {submitStatus === 'success' && (
                            <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                <div className="flex items-start gap-3">
                                    <span className="material-icons-round text-green-600 text-sm mt-0.5">check_circle</span>
                                    <div>
                                        <p className="text-sm text-green-800 dark:text-green-200 font-medium">
                                            Message Sent Successfully via EmailJS!
                                        </p>
                                        <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                                            Your message has been automatically delivered to mdazaharul725@gmail.com. I'll respond within 24 hours!
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Error Message */}
                        {submitStatus === 'error' && (
                            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <div className="flex items-start gap-3">
                                    <span className="material-icons-round text-red-600 text-sm mt-0.5">error</span>
                                    <div>
                                        <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                                            EmailJS Delivery Failed
                                        </p>
                                        <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                                            Please check EmailJS configuration or contact me directly at mdazaharul725@gmail.com
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        First Name
                                    </label>
                                    <input
                                        type="text"
                                        id="firstName"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-3 bg-gray-50 dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                                        placeholder="John"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Last Name
                                    </label>
                                    <input
                                        type="text"
                                        id="lastName"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-3 bg-gray-50 dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                                        placeholder="Doe"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                                    placeholder="john@example.com"
                                />
                            </div>

                            <div>
                                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Subject
                                </label>
                                <input
                                    type="text"
                                    id="subject"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                                    placeholder="Project Discussion"
                                />
                            </div>

                            <div>
                                <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Message
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    rows="5"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-4 py-3 bg-gray-50 dark:bg-black/20 border border-gray-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 resize-none"
                                    placeholder="Tell me about your project..."
                                ></textarea>
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-lg font-semibold hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmitting ? (
                                    <>
                                        Sending via EmailJS...
                                        <span className="material-icons-round text-sm animate-spin">sync</span>
                                    </>
                                ) : (
                                    <>
                                        Send Message
                                        <span className="material-icons-round text-sm">send</span>
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {/* Additional Info */}
            <div className="mt-16 text-center">
                <div className="inline-flex items-center gap-2 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark px-4 py-2 rounded-full">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                        Messages delivered instantly via EmailJS to mdazaharul725@gmail.com
                    </span>
                </div>
            </div>
        </section>
    );
};

export default Contact;